import React, { FC, ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import { BpDesignSystem } from '../../types';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { styled } from '../../styles';

export enum StatusChipType {
  GREY = 'GREY',
  TRANSPARENT = 'TRANSPARENT',
  DARK_GREY = 'DARK_GREY',
  YELLOW = 'YELLOW',
  LIGHT_YELLOW = 'LIGHT_YELLOW',
  RED = 'RED',
  LIGHT_RED = 'LIGHT_RED',
  BLUE = 'BLUE',
  LIGHT_BLUE = 'LIGHT_BLUE',
  LIGHT_PURPLE = 'LIGHT_PURPLE',
  GREEN = 'GREEN',
  LIGHT_GREEN = 'LIGHT_GREEN',
  DISABLED = 'DISABLED',
  DISABLED_SELECTED = 'DISABLED_SELECTED'
}

export interface StatusChipColor {
  color: string;
  backgroundColor: string;
  opacity?: number;
}

export const statusChipColors: Record<StatusChipType, StatusChipColor> = {
  DISABLED: { color: tokens.paletteNeutral50, backgroundColor: tokens.paletteNeutral95 },
  DISABLED_SELECTED: {
    color: tokens.colorOnPrimaryContainer,
    backgroundColor: tokens.colorPrimaryContainer,
    opacity: 0.8
  },
  GREY: { color: tokens.colorOnSurfaceVariant, backgroundColor: tokens.colorSurfaceVariant },
  DARK_GREY: { color: tokens.paletteNeutral20, backgroundColor: tokens.paletteNeutral70 },
  YELLOW: { color: tokens.colorOnWarning, backgroundColor: tokens.colorWarning },
  LIGHT_YELLOW: { color: tokens.colorOnWarningContainer, backgroundColor: tokens.colorWarningContainer },
  RED: { color: tokens.colorOnAlert, backgroundColor: tokens.colorAlert },
  LIGHT_RED: { color: tokens.colorOnAlertContainer, backgroundColor: tokens.colorAlertContainer },
  BLUE: { color: tokens.colorOnPrimary, backgroundColor: tokens.colorPrimary },
  LIGHT_BLUE: { color: tokens.colorOnPrimaryContainer, backgroundColor: tokens.colorPrimaryContainer },
  LIGHT_PURPLE: { color: tokens.colorOnInformationalContainer, backgroundColor: tokens.colorInformationalContainer },
  GREEN: { color: tokens.colorOnSuccess, backgroundColor: tokens.colorSuccess },
  LIGHT_GREEN: { color: tokens.colorOnSuccessContainer, backgroundColor: tokens.colorSuccessContainer },
  TRANSPARENT: { color: 'inherit', backgroundColor: 'transparent' }
} as const;

export type StatusChipProps = BpDesignSystem.Props<{
  label: ReactNode;
  type?: StatusChipType;
  uppercase?: boolean;
}>;

export const StatusChip: FC<StatusChipProps> = ({
  label,
  dataTest,
  type = StatusChipType.GREY,
  uppercase,
  ...props
}) => <StyledChip label={label} type={type} data-test={dataTest} $uppercase={uppercase} {...props} />;

const StyledChip = styled(Chip)<{ type: StatusChipType; $uppercase?: boolean }>`
  && {
    font-weight: 500;
    font-size: 12px;
    padding: 1px 16px;
    background: ${(props) => statusChipColors[props.type].backgroundColor};
    opacity: ${(props) => (statusChipColors[props.type] as (typeof statusChipColors)['DISABLED_SELECTED']).opacity ?? 1};
    border-radius: 10px;
    line-height: 18px;
    height: auto;
    cursor: inherit;

    span {
      padding: 0;
      text-transform: ${({ $uppercase }) => ($uppercase ? 'uppercase' : 'none')};
      color: ${(props) => statusChipColors[props.type].color};
    }
  }
`;
